import { LocationTemplateProps } from 'src/interfaces/Location'

const siteUrl = 'https://www.asktia.com'
const organization = {
  '@type': 'MedicalOrganization',
  name: 'Tia',
  url: siteUrl,
}
const logoUrl = `${siteUrl}/tia-logo.png`
const locationHubSlugs = new Set([
  'tia-la',
  'tia-los-angeles',
  'ny',
  'nyc',
  'tia-ny',
  'tia-nyc',
  'tia-new-york',
  'az',
  'tia-az',
  'tia-arizona',
  'tia-phoenix',
])

interface ParsedAddress {
  streetAddress: string
  addressLocality: string
  addressRegion: string
  postalCode: string
}

interface GeoCoordinates {
  latitude?: number
  longitude?: number
}

interface OpeningHours {
  dayOfWeek?: string
  opens?: string
  closes?: string
}

const parseAddress = (address?: string): ParsedAddress | null => {
  if (!address) return null

  const lines = address
    .split(/\r?\n/)
    .map(part => part.trim())
    .filter(Boolean)

  if (lines.length > 1) {
    const streetAddress = lines
      .slice(0, -1)
      .join(' ')
      .replace(/\s+/g, ' ')
      .replace(/\s+,/g, ',')
      .trim()
    const cityStateZip = lines[lines.length - 1].match(
      /^(.+?),\s*([A-Z]{2})\s+(\d{5}(?:-\d{4})?)$/
    )

    if (!streetAddress || !cityStateZip) return null

    return {
      streetAddress,
      addressLocality: cityStateZip[1],
      addressRegion: cityStateZip[2],
      postalCode: cityStateZip[3],
    }
  }

  const cleanAddress = address
    .replace(/\s+/g, ' ')
    .replace(/,\s*,/g, ',')
    .trim()
  const cityStateZip = cleanAddress.match(
    /^(.+?),\s*(.+?),\s*([A-Z]{2})(?:\s+|,\s*)(\d{5}(?:-\d{4})?)$/
  )

  if (!cityStateZip) return null

  return {
    streetAddress: cityStateZip[1],
    addressLocality: cityStateZip[2],
    addressRegion: cityStateZip[3],
    postalCode: cityStateZip[4],
  }
}

const buildPostalAddress = (address: ParsedAddress) => ({
  '@type': 'PostalAddress',
  streetAddress: address.streetAddress,
  addressLocality: address.addressLocality,
  addressRegion: address.addressRegion,
  postalCode: address.postalCode,
  addressCountry: 'US',
})

const buildGeoCoordinates = (geo?: GeoCoordinates) => {
  if (
    typeof geo?.latitude !== 'number' ||
    typeof geo?.longitude !== 'number'
  ) {
    return undefined
  }

  return {
    '@type': 'GeoCoordinates',
    latitude: geo.latitude,
    longitude: geo.longitude,
  }
}

const buildOpeningHoursSpecification = (openingHours?: OpeningHours[]) => {
  const specs = openingHours
    ?.filter(spec => spec.dayOfWeek && spec.opens && spec.closes)
    .map(spec => ({
      '@type': 'OpeningHoursSpecification',
      dayOfWeek: spec.dayOfWeek,
      opens: spec.opens,
      closes: spec.closes,
    }))

  return specs?.length ? specs : undefined
}

const getClinicName = (title: string) =>
  title
    .replace(/\s*\([^)]*\)\s*$/, '')
    .replace(/^tia\s+/i, '')
    .trim()

const buildClinicSchema = ({
  name,
  url,
  address,
  image,
  description,
  geo,
  telephone,
  openingHours,
  priceRange,
}: {
  name: string
  url: string
  address?: ParsedAddress | null
  image?: string
  description?: string
  geo?: GeoCoordinates
  telephone?: string
  openingHours?: OpeningHours[]
  priceRange?: string
}) => {
  const clinicName = getClinicName(name)
  const clinicSchema: any = {
    '@context': 'https://schema.org',
    '@type': 'MedicalClinic',
    '@id': `${url.replace(/\/$/, '')}/#clinic`,
    name: `Tia Women's Health Clinic ${clinicName}`,
    url,
    logo: logoUrl,
    parentOrganization: organization,
  }

  if (image) clinicSchema.image = image
  if (address) clinicSchema.address = buildPostalAddress(address)
  if (description) clinicSchema.description = description
  const geoCoordinates = buildGeoCoordinates(geo)
  if (geoCoordinates) clinicSchema.geo = geoCoordinates
  if (telephone) clinicSchema.telephone = telephone
  const openingHoursSpecification = buildOpeningHoursSpecification(openingHours)
  if (openingHoursSpecification) {
    clinicSchema.openingHoursSpecification = openingHoursSpecification
  }
  if (priceRange) clinicSchema.priceRange = priceRange

  return clinicSchema
}

export const generateLocationSchema = ({
  pageContext,
  pagePath,
}: {
  pageContext: LocationTemplateProps['pageContext']
  pagePath: string
}) => {
  const { main, meta } = pageContext
  const fullUrl = new URL(pagePath, siteUrl).toString()
  const address = parseAddress(main.address)
  const slug = main.slug?.current

  if (!address || (slug && locationHubSlugs.has(slug))) {
    return null
  }

  const schema: any = buildClinicSchema({
    name: main.title,
    url: fullUrl,
    address,
    image: main.image?.asset?.url,
    description: meta?.metaDescription,
    geo: main.geo,
    telephone: main.telephone,
    openingHours: main.openingHours,
    priceRange: main.priceRange,
  })

  return schema
}
